import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Edit, History, CheckCircle, XCircle, Play, Square } from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import { useToast } from '../../../../../context/ToastContext';
import detailedHistoryService from '../../../../../services/detailedHistory';

export default function ProcessTaskDetailedLog() {
  const { processTaskId } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  const { showError } = useToast();
  
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProcessTaskEvolution();
  }, [processTaskId]);

  const fetchProcessTaskEvolution = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await detailedHistoryService.getProcessTaskEvolutionHistory(processTaskId, authTokens.access);
      if (result.success) {
        setHistory(result.data.results);
      } else {
        setError(result.error);
        showError(result.error || 'Erreur lors du chargement de l\'évolution de la tâche');
      }
    } catch (err) {
      setError('Erreur de connexion');
      showError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) {
      return 'null';
    }
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return `[${value.map(item => renderValue(item)).join(', ')}]`;
      }
      // Handle objects by showing key properties
      if (value.task_name) return value.task_name;
      if (value.process_name) return value.process_name;
      if (value.email) return value.email;
      if (value.name) return value.name;
      return JSON.stringify(value);
    }
    return String(value);
  };

  const getActionIcon = (actionType) => {
    const iconConfig = {
      'create': { icon: Edit, color: 'text-green-600', bgColor: 'bg-green-100' },
      'update': { icon: Edit, color: 'text-blue-600', bgColor: 'bg-blue-100' },
      'delete': { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100' },
      'accept': { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100' },
      'reject': { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100' },
      'start': { icon: Play, color: 'text-blue-600', bgColor: 'bg-blue-100' },
      'complete': { icon: CheckCircle, color: 'text-purple-600', bgColor: 'bg-purple-100' },
      'status_change': { icon: Edit, color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
      'assign': { icon: User, color: 'text-indigo-600', bgColor: 'bg-indigo-100' }
    };

    const config = iconConfig[actionType] || { icon: Edit, color: 'text-gray-600', bgColor: 'bg-gray-100' };
    const IconComponent = config.icon;
    
    return (
      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${config.bgColor}`}>
        <IconComponent className={`h-4 w-4 ${config.color}`} />
      </div>
    );
  };

  const getActionLabel = (actionType) => {
    const labels = {
      'create': 'Création',
      'update': 'Modification',
      'delete': 'Suppression',
      'accept': 'Acceptée',
      'reject': 'Rejetée',
      'start': 'Démarrée',
      'complete': 'Terminée',
      'status_change': 'Changement de statut',
      'assign': 'Assignée'
    };
    return labels[actionType] || actionType;
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <span className="text-sm text-gray-500 italic">Aucun détail</span>;
    }

    return (
      <div className="mt-2 space-y-1">
        {Object.entries(changes).map(([key, value]) => (
          <div key={key} className="text-xs text-gray-600">
            <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
            <span className="text-gray-800">{renderValue(value)}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 text-gray-500 hover:text-gray-700 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Évolution de la Tâche de Processus</h1>
          <p className="text-sm text-gray-500">Chronologie complète des modifications</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button 
            onClick={fetchProcessTaskEvolution}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        {history.length === 0 && !loading ? (
          <div className="text-center py-12">
            <History className="h-10 w-10 mx-auto text-gray-400 mb-2" />
            <p className="text-lg font-medium text-gray-500">Aucun historique trouvé</p>
            <p className="text-sm text-gray-400 mt-1">Il n'y a pas encore d'historique pour cette tâche.</p>
          </div>
        ) : (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Chronologie des Événements</h2>
            
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              
              <div className="space-y-6">
                {history.map((entry, index) => (
                  <div key={entry.id} className="relative flex items-start space-x-4">
                    {/* Timeline dot */}
                    <div className="relative z-10 flex-shrink-0">
                      {getActionIcon(entry.action_type)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0 pb-6">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-semibold text-gray-900">
                            {getActionLabel(entry.action_type)}
                          </h3>
                          <span className="text-xs text-gray-500">
                            {new Date(entry.action_date).toLocaleString('fr-FR')}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-2">{entry.description}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <div className="flex items-center">
                              <Square className="h-3 w-3 mr-1" />
                              <span>Tâche: {entry.process_task_name}</span>
                            </div>
                            <div className="flex items-center">
                              <Square className="h-3 w-3 mr-1" />
                              <span>Processus: {entry.process_name}</span>
                            </div>
                            <div className="flex items-center">
                              <User className="h-3 w-3 mr-1" />
                              <span>Assignée à: {entry.assigned_to_email}</span>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            <div className="flex items-center">
                              <User className="h-3 w-3 mr-1" />
                              <span>Dernière mise à jour par: {entry.done_by_email || 'Système'}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Changes details */}
                        {renderChanges(entry.changes_summary)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button 
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Retour
        </button>
      </div>
    </div>
  );
}
